// AI Operator résumé card — used in Hero swipe pile and (later) Roster section.
// White card on dark stage = the dramatic product-UI moment per design system.

import Portrait from './Portrait';
import type { Operator } from '../data/roster';

type Props = { op: Operator };

const STATUS_COLORS = {
  'hiring-now':   { fg: '#2F6B4D', bg: '#E0EFE6' },
  'interviewing': { fg: '#C06E47', bg: '#F5E8DF' },
  'planned':      { fg: '#78716C', bg: '#F5F4F2' },
} as const;

const STATUS_LABEL = {
  'hiring-now':   '● PRODUCTION',
  'interviewing': '◐ BETA',
  'planned':      '○ TRAINING',
} as const;

export default function OperatorCard({ op }: Props) {
  const status = STATUS_COLORS[op.status];
  return (
    <>
      {/* AI OPERATOR banner — tech metadata, not "application" */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, paddingBottom: 10, borderBottom: '1px dashed #E8E6E1' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, letterSpacing: '0.1em', color: '#1A1918', textTransform: 'uppercase' }}>
          <span style={{ width: 6, height: 6, background: '#D4835E', borderRadius: 1, transform: 'rotate(45deg)' }}/>
          AI OPERATOR · {op.build}
        </span>
        <span
          style={{
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: 9, fontWeight: 700,
            color: status.fg, background: status.bg,
            padding: '3px 7px', borderRadius: 4, letterSpacing: '0.05em',
          }}
        >
          {STATUS_LABEL[op.status]}
        </span>
      </div>

      {/* Photo with copper ring + AI hex chip overlay */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ position: 'relative', flexShrink: 0, width: 56, height: 56 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            padding: 2,
            background: 'linear-gradient(135deg, #D4835E 0%, rgba(212,131,94,0.20) 100%)',
            boxSizing: 'border-box',
          }}>
            <Portrait src={op.photo} name={op.name} size={52}/>
          </div>
          <div
            style={{
              position: 'absolute', bottom: -3, right: -3,
              width: 20, height: 20,
              background: '#1A1918', color: '#D4835E',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'IBM Plex Mono, monospace',
              fontSize: 8, fontWeight: 700, letterSpacing: '0.04em',
              border: '1.5px solid #D4835E',
              borderRadius: 4,
              transform: 'rotate(45deg)',
            }}
          >
            <span style={{ transform: 'rotate(-45deg)', display: 'inline-block' }}>AI</span>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1A1918', fontFamily: 'Fraunces, serif' }}>{op.name}</div>
          <div style={{ fontSize: 11.5, color: '#57534E' }}>{op.role} · AI Role</div>
        </div>
      </div>

      <div style={{ fontSize: 12.5, color: '#44403C', marginTop: 12, lineHeight: 1.5 }}>{op.hook}</div>

      {/* Tech metadata row — model + uptime */}
      <div style={{ display: 'flex', gap: 10, marginTop: 12, paddingTop: 10, borderTop: '1px dashed #E8E6E1', fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, color: '#78716C', letterSpacing: '0.02em' }}>
        <span>model · <b style={{ color: '#1A1918', fontWeight: 600 }}>{op.model}</b></span>
        <span style={{ color: '#D6D3D1' }}>·</span>
        <span>uptime · <b style={{ color: op.status === 'hiring-now' ? '#2F6B4D' : '#C06E47', fontWeight: 600 }}>{op.uptime}</b></span>
      </div>

      {/* Cost / speed / quality row */}
      <div style={{ display: 'flex', gap: 14, marginTop: 8, fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#78716C' }}>
        <span><b style={{ color: '#1A1918' }}>{op.cost}</b>/run</span>
        <span><b style={{ color: '#1A1918' }}>{op.speed}</b> avg</span>
        <span>Q <b style={{ color: '#4ADE80', fontWeight: 700 }}>{op.quality}</b></span>
      </div>
    </>
  );
}
